import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, LogOut, Home, MessageSquare, Trash2, Edit2, 
  Eye, EyeOff, Link as LinkIcon, X, Loader2, Image as ImageIcon,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import StarRating from '@/components/StarRating';
import { 
  getAdminTestimonials, 
  createTestimonial, 
  updateTestimonial, 
  deleteTestimonial,
  getWebSocketUrl 
} from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const initialFormState = {
  title: '',
  description: '',
  rating: 5,
  time_to_finish: '',
  image_urls: ['', '', '', ''],
  published: false,
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
    setupWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await getAdminTestimonials();
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    try {
      const ws = new WebSocket(getWebSocketUrl());
      
      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onclose = () => {
        setWsConnected(false);
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        
        if (message.type === 'testimonial_created') {
          setTestimonials(prev => [message.data, ...prev]);
        } else if (message.type === 'testimonial_updated') {
          setTestimonials(prev => 
            prev.map(t => t.id === message.data.id ? message.data : t)
          );
        } else if (message.type === 'testimonial_deleted') {
          setTestimonials(prev => prev.filter(t => t.id !== message.data.id));
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const openCreateDialog = () => {
    setEditingTestimonial(null);
    setFormData(initialFormState);
    setIsDialogOpen(true);
  };

  const openEditDialog = (testimonial) => {
    setEditingTestimonial(testimonial);
    // Pad image_urls array to 4 slots
    const imageUrls = [...(testimonial.image_urls || [])];
    while (imageUrls.length < 4) {
      imageUrls.push('');
    }
    setFormData({
      title: testimonial.title,
      description: testimonial.description,
      rating: testimonial.rating,
      time_to_finish: testimonial.time_to_finish,
      image_urls: imageUrls,
      published: testimonial.published,
    });
    setIsDialogOpen(true);
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...formData.image_urls];
    newUrls[index] = value;
    setFormData(prev => ({ ...prev, image_urls: newUrls }));
  };

  const removeImageUrl = (index) => {
    const newUrls = [...formData.image_urls];
    newUrls[index] = '';
    setFormData(prev => ({ ...prev, image_urls: newUrls }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Filter out empty image URLs
      const filteredImageUrls = formData.image_urls.filter(url => url.trim() !== '');
      
      const submitData = {
        title: formData.title,
        description: formData.description,
        rating: formData.rating,
        time_to_finish: formData.time_to_finish,
        image_urls: filteredImageUrls,
        published: formData.published,
      };

      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial.id, submitData);
        toast.success('Testimonial updated successfully!');
      } else {
        await createTestimonial(submitData);
        toast.success('Testimonial created successfully!');
      }
      
      setIsDialogOpen(false);
      fetchTestimonials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Failed to save testimonial');
      } else {
        toast.error(errorMsg || 'Failed to save testimonial');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (testimonial) => {
    setTestimonialToDelete(testimonial);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!testimonialToDelete) return;
    
    try {
      await deleteTestimonial(testimonialToDelete.id);
      toast.success('Testimonial deleted successfully!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    } finally {
      setDeleteDialogOpen(false);
      setTestimonialToDelete(null);
    }
  };

  const togglePublish = async (testimonial) => {
    try {
      await updateTestimonial(testimonial.id, {
        ...testimonial,
        published: !testimonial.published,
      });
      toast.success(testimonial.published ? 'Testimonial unpublished' : 'Testimonial published!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const isValidUrl = (url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="admin-sidebar w-64 fixed left-0 top-0 h-full border-r border-white/5 p-6 flex flex-col" data-testid="admin-sidebar">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-white">Luminare Studio</h1>
          <p className="text-xs text-zinc-500 mt-1">Admin Dashboard</p>
        </div>
        
        <nav className="space-y-2 flex-1">
          <a 
            href="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5 text-white"
            data-testid="nav-testimonials"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Testimonials</span>
          </a>
        </nav>
        
        <div className="space-y-2 pt-4 border-t border-white/5">
          <a 
            href="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="nav-view-site"
          >
            <Home className="w-4 h-4" />
            <span>View Site</span>
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/5 w-full transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8" data-testid="admin-main">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-white">Testimonials</h2>
              <p className="text-zinc-500 text-sm mt-1">
                Manage your client testimonials and project showcases
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  wsConnected ? "bg-green-500" : "bg-zinc-500"
                )} />
                <span className="text-xs text-zinc-500">
                  {wsConnected ? 'Live' : 'Offline'}
                </span>
              </div>
              <Button
                onClick={openCreateDialog}
                className="cta-button rounded-lg"
                data-testid="create-testimonial-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Testimonial
              </Button>
            </div>
          </div>

          {/* Testimonials Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="glass rounded-xl h-48 animate-pulse" />
              ))}
            </div>
          ) : testimonials.length === 0 ? (
            <Card className="glass border-white/10">
              <CardContent className="py-16 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-400">No testimonials yet</p>
                <p className="text-zinc-500 text-sm mt-1">Create your first testimonial to get started</p>
                <Button
                  onClick={openCreateDialog}
                  className="mt-4 cta-button rounded-lg"
                  data-testid="empty-create-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Testimonial
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((testimonial) => (
                <Card 
                  key={testimonial.id} 
                  className="glass border-white/10 hover:border-white/20 transition-colors"
                  data-testid={`admin-testimonial-${testimonial.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-white line-clamp-1">
                          {testimonial.title}
                        </CardTitle>
                        <div className="flex items-center gap-3 mt-2">
                          <StarRating rating={testimonial.rating} size="small" />
                          <span className="text-xs text-zinc-500 mono">
                            {testimonial.time_to_finish}
                          </span>
                        </div>
                      </div>
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-medium",
                        testimonial.published ? "status-published" : "status-draft"
                      )}>
                        {testimonial.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-400 line-clamp-2 mb-4">
                      {testimonial.description}
                    </p>
                    
                    {testimonial.image_urls?.length > 0 && (
                      <div className="flex gap-2 mb-4">
                        {testimonial.image_urls.slice(0, 4).map((img, idx) => (
                          <div 
                            key={idx}
                            className="w-12 h-12 rounded-lg overflow-hidden border border-white/10"
                          >
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/48?text=!';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePublish(testimonial)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`toggle-publish-${testimonial.id}`}
                      >
                        {testimonial.published ? (
                          <><EyeOff className="w-4 h-4 mr-1" /> Unpublish</>
                        ) : (
                          <><Eye className="w-4 h-4 mr-1" /> Publish</>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(testimonial)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-${testimonial.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(testimonial)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`delete-${testimonial.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-xl bg-[#0a0a0a] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="testimonial-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">
              {editingTestimonial ? 'Edit Testimonial' : 'Create Testimonial'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editingTestimonial ? 'Update testimonial details below.' : 'Add a new testimonial with image URLs.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Project title or client name"
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                required
                data-testid="form-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the project and client feedback..."
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 min-h-[120px]"
                required
                data-testid="form-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Rating</Label>
                <div className="pt-1">
                  <StarRating
                    rating={formData.rating}
                    size="large"
                    interactive
                    onChange={(rating) => setFormData(prev => ({ ...prev, rating }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Time to Complete</Label>
                <Input
                  value={formData.time_to_finish}
                  onChange={(e) => setFormData(prev => ({ ...prev, time_to_finish: e.target.value }))}
                  placeholder="e.g., 2 weeks"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                  required
                  data-testid="form-time"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <Label className="text-zinc-300">Image URLs (max 4)</Label>
              <p className="text-xs text-zinc-500">Add direct image URLs from the web</p>
              
              {formData.image_urls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <div className="relative flex-1">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={url}
                      onChange={(e) => handleImageUrlChange(index, e.target.value)}
                      placeholder={`Image URL ${index + 1}`}
                      className={cn(
                        "pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-500",
                        url && !isValidUrl(url) && "border-red-500"
                      )}
                      data-testid={`form-image-url-${index}`}
                    />
                  </div>
                  {url && (
                    <>
                      <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-3 flex items-center text-zinc-400 hover:text-white border border-white/10 rounded-md"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeImageUrl(index)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`remove-url-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
              
              {/* Image Preview */}
              {formData.image_urls.some(url => url && isValidUrl(url)) && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {formData.image_urls.filter(url => url && isValidUrl(url)).map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                      <img
                        src={url}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/100?text=Invalid';
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                  data-testid="form-published-switch"
                />
                <Label className="text-zinc-300">Publish immediately</Label>
              </div>
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-white/10 text-zinc-300 hover:bg-white/5"
                data-testid="form-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="cta-button"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/10" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Testimonial</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete &quot;{testimonialToDelete?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-zinc-300 hover:bg-white/5" data-testid="delete-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
